import type { FeatureLike } from 'ol/Feature';
import Feature from 'ol/Feature';
import { Fill, Icon, Text } from 'ol/style';
import Style from 'ol/style/Style';

import { isNonEmptyString } from '@nora-monorepo/shared';
import { appLogger } from '@nora-monorepo/ui-shared';

import type { FeatureStyleProperties } from '#nora-ui/map/feature-properties';
import { getFeatureProperties } from '#nora-ui/map/feature-properties';
import { defaultFillColor } from '#nora-ui/map/map/DrawToolUIManager';
import { FeatureStyleManager } from '#nora-ui/map/map/FeatureStyleManager';
import { useSymbolStore } from '#nora-ui/stores/symbol-store';

const DEFAULT_ICON = 'question_mark';

interface GetSymbolStyleOptions {
  symbolPackId: string;
  symbolId: string;
  fillColor?: string;
  onLoad?: () => void;
}

export class SymbolStyleManager {
  /**
   * Renders
   * @param feature
   */
  static renderSymbolFromFeature(feature: FeatureLike): Style[] {
    const { symbolPackId, symbolId, fillColor, nameVisible, name, textColor } = getFeatureProperties<FeatureStyleProperties>(feature);

    const symbolGraphic = SymbolStyleManager.getSymbolGraphic({
      symbolPackId: symbolPackId ?? '',
      symbolId: symbolId ?? '',
      fillColor,
      onLoad: () => {
        if (feature instanceof Feature) {
          feature.changed();
        }
      },
    });

    const graphic = Array.isArray(symbolGraphic) ? symbolGraphic : [symbolGraphic];

    if (nameVisible) {
      return FeatureStyleManager.addStaticLabelWithText(name ?? '', graphic, textColor);
    }
    else {
      return graphic;
    }
  }

  /**
   * This is the core symbol graphic. The symbol itself.
   * @param options
   */
  static getSymbolGraphic(options: GetSymbolStyleOptions): Style {
    const { symbolPackId, symbolId, fillColor, onLoad } = options;

    const symbolStore = useSymbolStore();
    const cachedUrl = symbolStore.getCachedUrl(symbolPackId, symbolId, fillColor);

    if (!isNonEmptyString(cachedUrl)) {
      // Trigger async load - when complete, notify the feature to re-render
      symbolStore.getSymbolObjectUrl(symbolPackId, symbolId, fillColor)
        .then(() => {
          if (onLoad) {
            onLoad();
          }
        })
        .catch((error) => {
          appLogger.warn({
            message: 'Symbol graphic load failed',
            sensitiveContext: {
              symbolPackId,
              symbolId,
              error: error instanceof Error ? { name: error.name, message: error.message } : { value: String(error) },
            },
          });
        });

      return SymbolStyleManager.getPlaceholderGraphic();
    }

    return new Style({
      image: new Icon({
        width: 24,
        height: 24,
        anchor: [0.5, 0.5],
        anchorXUnits: 'fraction',
        anchorYUnits: 'fraction',
        src: cachedUrl,
      }),
    });
  }

  /**
   * Renders a placeholder, with attached label if set to visible.
   * @param feature
   * @private
   */
  static renderPlaceholderFromFeature(feature: FeatureLike) {
    const graphic = SymbolStyleManager.getPlaceholderGraphic();
    const { nameVisible, name, textColor } = getFeatureProperties<FeatureStyleProperties>(feature);

    if (nameVisible) {
      return FeatureStyleManager.addStaticLabelWithText(name ?? '', [graphic], textColor);
    }
    else {
      return graphic;
    }
  };

  /**
   * Renders a fontIcon feature, with or without a text label
   * @param feature
   */
  static renderFontIconFromFeature(feature: FeatureLike): Style[] {
    const { fillColor, fontIcon, textColor, nameVisible, name, iconStrokeColor } = getFeatureProperties<FeatureStyleProperties>(feature);

    const icon = FeatureStyleManager.genericMarkerIcon({
      icon: fontIcon ?? DEFAULT_ICON,
      iconFillColor: fillColor ?? defaultFillColor,
      iconStrokeColor,
      iconStrokeWidth: 0,
    });
    if (!nameVisible) {
      return icon;
    }
    else {
      return FeatureStyleManager.addStaticLabelWithText(name ?? '', icon, textColor);
    }
  }

  /**
   * Elementary placeholder icon (default icon).
   * @private
   */
  private static getPlaceholderGraphic(): Style {
    return new Style({
      text: new Text({
        font: '24px "grammar-icons"',
        text: DEFAULT_ICON,
        fill: new Fill({
          color: 'rgba(0, 0, 0, 1)',
        }),
      }),
    });
  }
}

export function symbolStyleFunction(feature: FeatureLike): Style | Style[] {
  const { symbolPackId, symbolId, fontIcon } = getFeatureProperties<FeatureStyleProperties>(feature);

  if (isNonEmptyString(fontIcon)) {
    return SymbolStyleManager.renderFontIconFromFeature(feature);
  }

  if (!isNonEmptyString(symbolPackId) || !isNonEmptyString(symbolId)) {
    return SymbolStyleManager.renderPlaceholderFromFeature(feature);
  }

  return SymbolStyleManager.renderSymbolFromFeature(feature);
}
